import { useState, useEffect } from 'react';
import { useTenant } from '@/Hooks/useTenant';
import { useUser } from '@/contexts/UserContext';
import { quizService } from '@/services/quizService';

export function useQuizModal() {
  const { config } = useTenant();
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [step, setStep] = useState('intro'); // 'intro' | 'questions' | 'done'
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const modalId = config?.FEATURE_FLAGS?.quizzes?.show_modal_id;

  useEffect(() => {
    if (!modalId || !user) return;
    const storageKey = `quiz_modal_shown_${modalId}`;
    if (sessionStorage.getItem(storageKey)) return;

    quizService.getQuizById(modalId)
      .then((data) => {
        if (!data) return;
        if (data.status === 'completed' || data.status === 'failed') {
          sessionStorage.setItem(storageKey, '1');
          return;
        }
        setQuiz(data);
        setIsOpen(true);
      })
      .catch(() => {});
  }, [modalId, user]);

  const handleClose = () => {
    sessionStorage.setItem(`quiz_modal_shown_${modalId}`, '1');
    setIsOpen(false);
  };

  const handleStart = () => {
    setStep('questions');
    setCurrentIndex(0);
  };

  const handleSelectAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleToggleCheckbox = (questionId, optionIndex) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      const exists = current.includes(optionIndex);
      return {
        ...prev,
        [questionId]: exists
          ? current.filter(i => i !== optionIndex)
          : [...current, optionIndex],
      };
    });
  };

  const handleNext = async () => {
    const questions = quiz?.questions || [];
    const isLast = currentIndex === questions.length - 1;

    if (!isLast) {
      setCurrentIndex(prev => prev + 1);
      return;
    }

    setSubmitting(true);
    try {
      const finalAnswers = { ...answers };
      questions.forEach(q => {
        if (Number(q.type) >= 2 && (!finalAnswers[q.id] || String(finalAnswers[q.id]).trim() === '')) {
          finalAnswers[q.id] = 'Não respondido';
        }
      });
      await quizService.submitQuizAnswers(quiz.id, finalAnswers, new Date());
    } catch {
      // não bloqueia o UX em caso de erro
    } finally {
      setSubmitting(false);
      setStep('done');
      sessionStorage.setItem(`quiz_modal_shown_${modalId}`, '1');
    }
  };

  const currentQuestion = quiz?.questions?.[currentIndex] ?? null;
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
  const isTextQuestion = currentQuestion !== null && Number(currentQuestion.type) >= 2;
  const hasAnswer =
    isTextQuestion ||
    (currentAnswer !== undefined &&
      currentAnswer !== '' &&
      !(Array.isArray(currentAnswer) && currentAnswer.length === 0));

  return {
    isOpen,
    quiz,
    step,
    currentIndex,
    currentQuestion,
    answers,
    currentAnswer,
    hasAnswer,
    isTextQuestion,
    submitting,
    handleClose,
    handleStart,
    handleSelectAnswer,
    handleToggleCheckbox,
    handleNext,
  };
}
